import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../../components/Layout";
import Loader from "react-loader-advanced";
import LoadingSpin from "react-loading-spin";
import config from "../../assets/config";
import BarChart from "../BarChart";
import LineChart from "../LineChart";
import DoughnutChart from "../DoughnutChart";
import { Nav, NavItem, NavLink, TabContent, TabPane, Container, Row, Col } from "reactstrap"



function Resource({ id, location }) {

    const { state } = location;
    const [item, setItem] = useState(state && state.dataset ? state.dataset : {});
    const [title, setTitle] = useState(true);
    // Con `id` en la URL cargamos directo; sin `id` mostramos el selector de
    // datasets y no arrancamos en modo cargando.
    const [loading, setLoading] = useState(Boolean(id) || Boolean(state && state.dataset));
    const [activeTab, setActiveTab] = useState('1');

    // --- Selector de datasets (solo cuando se entra sin id por la URL) --------
    // Lista de datasets del portal para poblar el desplegable.
    const [datasets, setDatasets] = useState([]);
    // identifier de la distribucion (recurso CSV) que se esta graficando.
    const [resourceId, setResourceId] = useState(id || null);
    // Mensaje cuando el dataset elegido no tiene un recurso graficable (CSV).
    const [selectorError, setSelectorError] = useState('');

    // Carga la lista de datasets desde el endpoint rapido del buscador.
    useEffect(() => {
        // Solo si NO venimos con un id/estado concreto (modo selector).
        if (id || (state && state.dataset)) {
            return;
        }
        // Endpoint propio que lista TODOS los datasets con recurso CSV (sin el
        // tope de 100 de search-fast). Devuelve { total, results:[{identifier,title}] }
        // ya ordenados por titulo.
        axios.get(`/api/graficables`)
            .then((res) => {
                const results = (res.data && res.data.results) || [];
                setDatasets(results);
            })
            .catch(() => setDatasets([]));
    }, [id, state]);

    // Carga de datos para graficar, a partir del resourceId (distribucion CSV).
    useEffect(() => {
        if (state && state.dataset) {
            setLoading(false);
            return;
        }
        const rid = resourceId;
        if (!rid) {
            return;
        }
        setLoading(true);
        axios.get(`${process.env.REACT_APP_ROOT_URL}/metastore/schemas/distribution/items/${rid}`)
            .then((res) => setTitle(res.data.data.title))
            .catch(() => setTitle(''));
        axios.get(`${process.env.REACT_APP_ROOT_URL}/datastore/sql/?query=[SELECT%20*%20FROM%20${rid}][LIMIT%20100];&show-db-columns`)
            .then((res) => {
                setItem(res.data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [resourceId, state]);

    // Al elegir un dataset en el desplegable: resolver su primer recurso CSV.
    const onSelectDataset = (datasetId) => {
        setSelectorError('');
        setItem({});
        setTitle(true);
        setResourceId(null);
        if (!datasetId) {
            return;
        }
        setLoading(true);
        axios.get(`${process.env.REACT_APP_ROOT_URL}/metastore/schemas/dataset/items/${datasetId}?show-reference-ids`)
            .then((res) => {
                const distribution = (res.data && res.data.distribution) || [];
                // Buscar la primera distribucion CSV (la unica graficable).
                const csv = distribution
                    .map((dist) => dist.data || dist)
                    .find((d) => (d.format || '').toLowerCase() === 'csv');
                // El identifier del recurso: en show-reference-ids viene como
                // identifier de la distribucion referenciada.
                const ref = distribution.find((dist) =>
                    ((dist.data && dist.data.format) || dist.format || '').toLowerCase() === 'csv'
                );
                const rid = ref && (ref.identifier || (ref.data && ref.data.identifier));
                if (!csv || !rid) {
                    setLoading(false);
                    setSelectorError('El conjunto de datos seleccionado no tiene un recurso CSV que se pueda graficar.');
                    return;
                }
                setResourceId(rid);
            })
            .catch(() => {
                setLoading(false);
                setSelectorError('No se pudieron cargar los recursos de este conjunto de datos.');
            });
    };

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // Modo "embed": /resource/{id}?embed=1 renderiza SOLO la gráfica, sin la
    // cabecera/pie del portal, para poder incrustarla en un iframe dentro de una
    // historia. También oculta el selector (el dataset viene fijado por la URL).
    const params = new URLSearchParams((location && location.search) || '');
    const embed = params.get('embed') === '1';
    // Configuración fijada por el autor al insertar la gráfica en una historia:
    // tipo (bar|line|doughnut) y columnas de datos (s1, s2). Si vienen, el embed
    // muestra ESA gráfica fija, sin pestañas ni selectores.
    const embedType = params.get('type');
    const embedS1 = params.get('s1') || undefined;
    const embedS2 = params.get('s2') || undefined;

    // Si entramos sin id por la URL, mostramos el selector arriba (nunca en embed).
    const mostrarSelector = !embed && !id && !(state && state.dataset);

    const selectorBlock = mostrarSelector && (
        <Container className="pt-5">
            <Row>
                <Col sm="12">
                    <p className="title title--black">Generar gráfica a partir de un conjunto de datos</p>
                    <div className="form-group">
                        <label className="paragraph-graph" htmlFor="dataset-select">
                            <b>Selecciona un conjunto de datos</b>
                        </label>
                        <select
                            id="dataset-select"
                            className="form-control paragraph-graph"
                            onChange={(e) => onSelectDataset(e.target.value)}
                            defaultValue=""
                        >
                            <option value="">Seleccione un conjunto de datos</option>
                            {datasets.map((d) => (
                                <option key={d.identifier} value={d.identifier}>{d.title}</option>
                            ))}
                        </select>
                        {datasets.length === 0 && (
                            <small className="paragraph-graph">Cargando conjuntos de datos…</small>
                        )}
                    </div>
                    {selectorError && (
                        <div className="alert alert-warning" role="alert">
                            <b>{selectorError}</b>
                        </div>
                    )}
                </Col>
            </Row>
        </Container>
    );

    const chartBlock = (resourceId || (state && state.dataset)) && (
        <Loader
            backgroundStyle={{ backgroundColor: "#f9fafb" }}
            foregroundStyle={{ backgroundColor: "#f9fafb" }}
            show={loading}
            message={
                <LoadingSpin width={"3px"} primaryColor={"#007BBC"} />
            }
        >
            <p class="title title--black p-5">{title}</p>
            <Container>
                <Row>
                    <Col sm="12">
                        <Nav tabs>
                            <NavItem>
                                <NavLink
                                    className={activeTab === '1' ? 'active' : ''}
                                    onClick={() => toggleTab('1')}
                                >
                                    <h4>Barras</h4>
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === '2' ? 'active' : ''}
                                    onClick={() => toggleTab('2')}
                                >
                                    <h4>Líneas</h4>
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={activeTab === '3' ? 'active' : ''}
                                    onClick={() => toggleTab('3')}
                                >
                                    <h4>Dona</h4>
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            <TabPane tabId="1">
                                <h4 className="pt-5">Gráfica de Barras</h4>
                                <div className="alert alert-primary m-5" role="alert">
                                    <h4 className="alert-heading"><b>¡Hola!</b></h4>
                                    <p className="paragraph-graph"><b>Para iniciar a graficar, por favor selecciona una serie.</b></p>
                                    <hr />
                                    <p className="mb-0 paragraph-graph"><b>Para una mejor interacción con la gráfica, puedes hacer uso del scroll del mouse o dar clic sobre la gráfica y arrastra el cursor para seleccionar el área sobre la que se desea hacer zoom.</b></p>
                                    <p className="mb-0 paragraph-graph"><b>De igual forma puedes hacer clic en el botón Reset Zoom para reestablecer la gráfica.</b></p>
                                </div>
                                <BarChart item={item} />
                            </TabPane>
                            <TabPane tabId="2">
                                <h4 className="pt-5">Gráfica de Líneas</h4>
                                <div className="alert alert-primary m-5" role="alert">
                                    <h4 className="alert-heading"><b>¡Hola!</b></h4>
                                    <p className="paragraph-graph"><b>Para iniciar a graficar, por favor selecciona una serie.</b></p>
                                    <hr />
                                    <p className="mb-0 paragraph-graph"><b>Para una mejor interacción con la gráfica, puedes hacer uso del scroll del mouse o dar clic sobre la gráfica y arrastra el cursor para seleccionar el área sobre la que se desea hacer zoom.</b></p>
                                    <p className="mb-0 paragraph-graph"><b>De igual forma puedes hacer clic en el botón Reset Zoom para reestablecer la gráfica.</b></p>
                                </div>
                                <LineChart item={item} />
                            </TabPane>
                            <TabPane tabId="3">
                                <h4 className="pt-5">Gráfica de Dona</h4>
                                <div className="alert alert-primary m-5" role="alert">
                                    <h4 className="alert-heading"><b>¡Hola!</b></h4>
                                    <p className="paragraph-graph"><b>Para iniciar a graficar, por favor selecciona una serie.</b></p>
                                </div>
                                <DoughnutChart item={item} />
                            </TabPane>
                        </TabContent>
                    </Col>
                </Row>
            </Container>
        </Loader>
    );

    // Gráfica FIJA para el embed (tipo + columnas elegidos por el autor).
    const fixedChart = (resourceId || (state && state.dataset)) && (
        <Loader
            backgroundStyle={{ backgroundColor: "#f9fafb" }}
            foregroundStyle={{ backgroundColor: "#f9fafb" }}
            show={loading}
            message={<LoadingSpin width={"3px"} primaryColor={"#007BBC"} />}
        >
            {embedType === 'line' && (
                <LineChart item={item} fixedS1={embedS1} fixedS2={embedS2} hideControls={true} />
            )}
            {embedType === 'doughnut' && (
                <DoughnutChart item={item} fixedS1={embedS1} hideControls={true} />
            )}
            {(!embedType || embedType === 'bar') && (
                <BarChart item={item} fixedS1={embedS1} fixedS2={embedS2} hideControls={true} />
            )}
        </Loader>
    );

    // En modo embed devolvemos SOLO la gráfica (sin Layout), para el iframe.
    // Si el autor fijó tipo/columnas, mostramos esa gráfica fija; si no, el
    // bloque interactivo con pestañas (retrocompatible).
    if (embed) {
        return (
            <div className="medata-chart-embed">
                {embedType ? fixedChart : chartBlock}
            </div>
        );
    }

    return (
        <Layout title="Datastore" headerClass='header header--small'>
            <div className={`dc-dataset-page ${config.container}`}>
                {selectorBlock}
                {chartBlock}
            </div>
        </Layout>
    );



}

export default Resource
